import { api } from './api';
import {
  saveProducts,
  saveDistributors,
  getSubmissionQueue,
  removeFromQueue,
  updateQueueItemRetryCount,
  isDataStale,
  getProducts,
  getDistributors,
} from './offlineStorage';
import type { QueuedSubmission } from './offlineStorage';

const MAX_RETRY_COUNT = 3;

/**
 * Prefetch essential data after login
 * This runs in the background and caches data for offline use
 */
export async function prefetchEssentialData(): Promise<void> {
  try {
    console.log('[Sync] Prefetching essential data...');
    
    // Fetch and cache products
    const productsRes = await api.get('/products?is_active=true');
    if (productsRes.data) {
      await saveProducts(productsRes.data);
      console.log(`[Sync] Cached ${productsRes.data.length} products`);
    }

    // Fetch and cache distributors
    const distributorsRes = await api.get('/distributors');
    if (distributorsRes.data) {
      await saveDistributors(distributorsRes.data);
      console.log(`[Sync] Cached ${distributorsRes.data.length} distributors`);
    }
    
    // Note: PoS list is NOT cached - too large and not needed offline
    // PoS selection must be done online (before entering store)
  } catch (error) {
    console.error('[Sync] Failed to prefetch data:', error);
    // Don't throw - prefetch failing shouldn't break the app
  }
}

/**
 * Refresh cached data if it's stale (older than 1 hour)
 */
export async function refreshDataIfStale(): Promise<void> {
  try {
    const productsStale = await isDataStale('products');
    const distributorsStale = await isDataStale('distributors');

    if (productsStale || distributorsStale) {
      console.log('[Sync] Data is stale, refreshing...');
      await prefetchEssentialData();
    }
  } catch (error) {
    console.error('[Sync] Failed to refresh stale data:', error);
  }
}

/**
 * Sync pending submissions when back online
 * Returns number of successfully synced items
 */
export async function syncPendingSubmissions(): Promise<{
  synced: number;
  failed: number;
  errors: string[];
}> {
  const queue = await getSubmissionQueue();
  
  if (queue.length === 0) {
    console.log('[Sync] No pending submissions');
    return { synced: 0, failed: 0, errors: [] };
  }

  console.log(`[Sync] Syncing ${queue.length} pending submissions...`);
  
  let synced = 0;
  let failed = 0;
  const errors: string[] = [];

  for (const submission of queue) {
    try {
      await syncSubmission(submission);
      await removeFromQueue(submission.id);
      synced++;
      console.log(`[Sync] Successfully synced ${submission.type} (${submission.id})`);
    } catch (error: any) {
      failed++;
      const errorMsg = error?.response?.data?.error || error?.response?.data?.details || error?.message || 'Unknown error';
      const errorDetails = error?.response?.data ? JSON.stringify(error.response.data) : error?.message;
      errors.push(`${submission.type}: ${errorMsg}`);
      console.error(`[Sync] Failed to sync ${submission.type}:`, errorDetails);
      console.error('[Sync] Submission data:', JSON.stringify(submission.data, null, 2));
      
      // Update retry count
      await updateQueueItemRetryCount(submission.id);
      
      // Remove if exceeded max retries
      if (submission.retryCount >= MAX_RETRY_COUNT) {
        console.warn(`[Sync] Removing ${submission.id} after ${MAX_RETRY_COUNT} failed attempts`);
        await removeFromQueue(submission.id);
      }
    }
  }

  console.log(`[Sync] Sync complete: ${synced} succeeded, ${failed} failed`);
  return { synced, failed, errors };
}

/**
 * Sync a single submission to the backend
 */
async function syncSubmission(submission: QueuedSubmission): Promise<void> {
  // Extract offline context if present (contains user_id, team_id)
  const { _offline_context, ...actualData } = submission.data;
  
  switch (submission.type) {
    case 'visit':
      await api.post('/visits', actualData);
      break;

    case 'first_time_visit':
      await api.post('/visits', actualData);
      break;

    case 'product_intro':
      await api.post('/visits', actualData);
      break;

    case 'revisit':
      await api.post('/visits', actualData);
      break;

    default:
      throw new Error(`Unknown submission type: ${submission.type}`);
  }
}

/**
 * Get current cache status for debugging/display
 */
export async function getCacheStatus(): Promise<{
  products: { count: number; cached: boolean };
  distributors: { count: number; cached: boolean };
  queueLength: number;
}> {
  const products = await getProducts();
  const distributors = await getDistributors();
  const queue = await getSubmissionQueue();

  return {
    products: {
      count: products?.length || 0,
      cached: products !== null,
    },
    distributors: {
      count: distributors?.length || 0,
      cached: distributors !== null,
    },
    queueLength: queue.length,
  };
}
